import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  NATIVE_BACK_EVENT,
  addConnectionStatusListener,
  addNativeBackButtonListener,
  addNativeAppStateListener,
  addNativeKeyboardListener,
  addNativeUrlListener,
  getConnectionStatus,
  getNativeLaunchUrl,
  hideNativeSplash,
  initializeNativeShell,
  isAndroid,
  isNative,
  minimizeNativeApp,
  openExternal,
  resolveAthenaDeepLink,
  shouldOpenLinkExternally,
} from './NativeBridge';
import {
  deliverAthenaFloatingOverlayQuickResult,
  observeAthenaFloatingOverlay,
  syncAthenaFloatingOverlay,
} from './AthenaFloatingOverlay';
import { submitAthenaQuickOverlayMessage } from './AthenaQuickOverlayRuntime';
import { queueAthenaIntent } from './AthenaIntentRouter';
import { ATHENA_EVENT_SOURCES, ATHENA_EVENT_TYPES, emitAthenaEvent } from '../events/AthenaEventBus';
import {
  observeNativeAthenaNotifications,
  registerAthenaNotifications,
  sanitizeAthenaNotificationRoute,
} from '../notifications/AthenaNotifications';
import { markNotifClicked } from '../utils/athenaNotifications';

const offlineBannerStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 100000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 34,
  padding: 'calc(env(safe-area-inset-top, 0px) + 8px) 16px 8px',
  background: 'rgba(12, 22, 43, 0.96)',
  borderBottom: '1px solid rgba(96, 165, 250, 0.25)',
  color: '#dbeafe',
  fontFamily: 'IBM Plex Sans, system-ui, sans-serif',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.01em',
  pointerEvents: 'none',
};

const PENDING_NOTIFICATION_ACTION_KEY = 'athena_pending_native_notification_action';

function actionIdentity(notification) {
  if (notification?.id) return `id:${notification.id}`;
  if (notification?.route) return `route:${notification.route}`;
  return null;
}

function persistPendingNotificationAction(notification) {
  if (typeof window === 'undefined' || !notification?.canOpen) return;
  try {
    // Keep only navigation metadata through the unauthenticated screen. Push
    // bodies are intentionally not copied into storage or a query string.
    window.sessionStorage.setItem(PENDING_NOTIFICATION_ACTION_KEY, JSON.stringify({
      id: notification.id || null,
      route: notification.route || '/',
      type: notification.type || 'system',
      contextId: notification.contextId || null,
      action: notification.action || 'open',
      canOpen: true,
    }));
  } catch {
    // Session storage can be disabled in a private WebView. The action still
    // remains safe to ignore instead of attempting an untrusted fallback.
  }
}

function consumePendingNotificationAction() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_NOTIFICATION_ACTION_KEY);
    window.sessionStorage.removeItem(PENDING_NOTIFICATION_ACTION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    const route = sanitizeAthenaNotificationRoute(value?.route);
    if (!value || value.canOpen !== true || !route) return null;
    return { ...value, route };
  } catch {
    return null;
  }
}

/**
 * Native-only application shell. It remains mounted inside BrowserRouter so
 * native deep links and Android's back button can use the existing React
 * routes without introducing a second navigation system.
 */
export function NativeAppShell({ appReady = false, authenticated = false, authToken = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const appReadyRef = useRef(appReady);
  const handledNotificationActionsRef = useRef(new Set());
  const pendingQuickRequestsRef = useRef(new Set());
  const [isConnected, setIsConnected] = useState(true);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    appReadyRef.current = appReady;
  }, [appReady]);

  const navigateFromUrl = useCallback((url, { replace = false } = {}) => {
    // `athena://action/*` is parsed by the closed Intent Router before route
    // resolution. This keeps a safe, one-time action through the login gate
    // without treating deep-link query data as an executable payload.
    queueAthenaIntent(url, { source: 'deep_link' });
    const route = resolveAthenaDeepLink(url);
    if (!route) return false;
    navigate(route, { replace });
    return true;
  }, [navigate]);

  const openNotificationAction = useCallback((notification) => {
    if (!notification?.canOpen) return false;

    const identity = actionIdentity(notification);
    if (identity && handledNotificationActionsRef.current.has(identity)) return true;

    emitAthenaEvent({
      type: ATHENA_EVENT_TYPES.NOTIFICATION_ACTION,
      source: ATHENA_EVENT_SOURCES.NOTIFICATION,
      metadata: {
        notificationId: notification.id || '',
        notificationType: notification.type || 'system',
        action: notification.action || 'open',
        canOpen: true,
      },
      idempotencyKey: `notification-action:${identity || notification.type || 'system'}`,
    });

    if (!authenticated) {
      persistPendingNotificationAction(notification);
      return false;
    }

    if (identity) handledNotificationActionsRef.current.add(identity);
    if (notification.id) markNotifClicked(notification.id);
    navigate(notification.route || '/', { replace: false });
    return true;
  }, [authenticated, navigate]);

  useEffect(() => {
    if (!isNative) return undefined;

    let active = true;
    const splashFallback = window.setTimeout(() => {
      if (active) void hideNativeSplash();
    }, 1800);

    void initializeNativeShell().then(() => {
      if (active && appReadyRef.current) void hideNativeSplash();
    });

    return () => {
      active = false;
      window.clearTimeout(splashFallback);
    };
  }, []); // Native platform identity does not change while the app is open.

  useEffect(() => {
    if (isNative && appReady) void hideNativeSplash();
  }, [appReady]);

  useEffect(() => {
    if (!isNative) return undefined;

    let active = true;
    let removeUrlListener = () => {};
    const handleUrl = ({ url }) => navigateFromUrl(url);

    void addNativeUrlListener(handleUrl).then((remove) => {
      if (active) removeUrlListener = remove;
      else void remove();
    });

    void getNativeLaunchUrl().then((url) => {
      if (active && url) navigateFromUrl(url, { replace: true });
    });

    return () => {
      active = false;
      void removeUrlListener();
    };
  }, [navigateFromUrl]);

  useEffect(() => {
    if (!isNative) return undefined;
    let active = true;
    let removeLifecycleListener = () => {};
    const syncFloatingOverlay = (foreground) => {
      // This reconciler has no permission side effect. Its bridge only shows
      // an Android overlay that the person explicitly enabled beforehand.
      // When Athena is foregrounded, it is always hidden.
      if (!isAndroid) return;
      void syncAthenaFloatingOverlay({
        authenticated,
        appState: foreground ? 'foreground' : 'background',
        visualState: authenticated ? 'idle' : 'offline',
      });
    };

    // Login/logout can rerun this effect while Android remains active. Hide a
    // stale surface immediately instead of waiting for the next lifecycle
    // transition.
    syncFloatingOverlay(true);
    void addNativeAppStateListener((state) => {
      if (!active) return;
      const foreground = state?.isActive === true;
      emitAthenaEvent({
        type: foreground ? ATHENA_EVENT_TYPES.APP_FOREGROUND : ATHENA_EVENT_TYPES.APP_BACKGROUND,
        source: ATHENA_EVENT_SOURCES.APP,
        metadata: { active: foreground },
        idempotencyKey: `app-lifecycle:${foreground ? 'foreground' : 'background'}:${Math.floor(Date.now() / 1000)}`,
      });
      syncFloatingOverlay(foreground);
    }).then((remove) => {
      if (active) removeLifecycleListener = remove;
      else void remove();
    });
    return () => {
      active = false;
      void removeLifecycleListener();
    };
  }, [authenticated]);

  useEffect(() => {
    if (!isAndroid) return undefined;

    let active = true;
    let removeOverlayListener = () => {};
    const pendingQuickRequests = pendingQuickRequestsRef.current;
    const handleQuickSubmit = async (event) => {
      if (!active || event?.type !== 'quick_submit') return;
      const requestId = event.requestId;
      const text = event.text;
      if (typeof requestId !== 'string' || typeof text !== 'string') return;
      if (pendingQuickRequests.has(requestId)) return;
      pendingQuickRequests.add(requestId);

      try {
        const outcome = authenticated
          ? await submitAthenaQuickOverlayMessage({ requestId, text })
          : {
            accepted: false,
            response: 'Abra a Athena e entre na sua conta antes de usar a janela rápida.',
          };
        if (!active) return;
        const response = typeof outcome?.response === 'string' ? outcome.response : '';
        await deliverAthenaFloatingOverlayQuickResult({
          requestId,
          success: outcome?.accepted === true && outcome?.queued !== true,
          response: response || (outcome?.queued === true
            ? 'A conversa está sendo restaurada. Abra a Athena e tente novamente em instantes.'
            : 'Não consegui concluir esta mensagem agora. Abra a Athena e tente novamente.'),
        });
      } finally {
        pendingQuickRequests.delete(requestId);
      }
    };

    void observeAthenaFloatingOverlay((event) => {
      void handleQuickSubmit(event);
    }).then((remove) => {
      if (active) removeOverlayListener = remove;
      else void remove();
    });

    return () => {
      active = false;
      void removeOverlayListener();
      pendingQuickRequests.clear();
    };
  }, [authenticated]);

  useEffect(() => {
    if (!isNative) return undefined;

    let active = true;
    let removeNotificationListeners = () => {};
    const handleReceived = (notification) => {
      emitAthenaEvent({
        type: ATHENA_EVENT_TYPES.NOTIFICATION_RECEIVED,
        source: ATHENA_EVENT_SOURCES.NOTIFICATION,
        metadata: {
          notificationId: notification?.id || '',
          notificationType: notification?.type || 'system',
          action: 'none',
          canOpen: notification?.canOpen === true,
        },
        idempotencyKey: `notification-received:${actionIdentity(notification) || notification?.type || 'system'}`,
      });
    };
    const handleAction = (notification) => {
      if (active) openNotificationAction(notification);
    };

    // Foreground payloads are emitted by the provider into the shared
    // notification log. This shell owns only navigation and token
    // reconciliation. It never asks native Firebase for a token at boot.
    void observeNativeAthenaNotifications({
      onReceived: handleReceived,
      onAction: handleAction,
      onTokenRefresh: () => {
        emitAthenaEvent({
          type: ATHENA_EVENT_TYPES.PUSH_TOKEN_REFRESH,
          source: ATHENA_EVENT_SOURCES.NOTIFICATION,
          metadata: {},
          idempotencyKey: `push-token-refresh:${Math.floor(Date.now() / 60000)}`,
        });
        if (!authToken) return;
        // The provider rechecks `pushEnabled` before it can call getToken, so
        // this is safe for a fresh install with FCM auto-init disabled.
        registerAthenaNotifications(authToken).catch(() => {});
      },
    }).then((remove) => {
      if (active) removeNotificationListeners = remove;
      else remove?.();
    });

    return () => {
      active = false;
      removeNotificationListeners?.();
    };
  }, [authToken, openNotificationAction]);

  useEffect(() => {
    if (!authenticated) return;
    const notification = consumePendingNotificationAction();
    if (notification) openNotificationAction(notification);
  }, [authenticated, openNotificationAction]);

  useEffect(() => {
    let active = true;
    let removeKeyboardListeners = () => {};
    void addNativeKeyboardListener(({ height, visible }) => {
      // O bridge atualiza a variável CSS do inset. Este marcador adicional é
      // consumido pelos compositores que precisam trocar para posicionamento
      // fixo quando um fabricante sobrepõe (em vez de redimensionar) a WebView.
      if (typeof document === 'undefined') return;
      document.documentElement.dataset.athenaKeyboard = visible && Number(height) > 0 ? 'open' : 'closed';
    }).then((remove) => {
      if (active) removeKeyboardListeners = remove;
      else void remove();
    });

    return () => {
      active = false;
      void removeKeyboardListeners();
    };
  }, []);

  useEffect(() => {
    if (!isNative) return undefined;

    let active = true;
    let removeConnectionListener = () => {};
    const updateStatus = (status) => {
      if (!active) return;
      const connected = status?.connected !== false;
      setIsConnected(connected);
      emitAthenaEvent({
        type: ATHENA_EVENT_TYPES.NETWORK_CHANGED,
        source: ATHENA_EVENT_SOURCES.NETWORK,
        metadata: {
          connected,
          connectionType: status?.connectionType || (connected ? 'unknown' : 'none'),
        },
        idempotencyKey: `network:${connected ? 'online' : 'offline'}:${status?.connectionType || 'unknown'}:${Math.floor(Date.now() / 1000)}`,
      });
    };

    void getConnectionStatus().then(updateStatus);
    void addConnectionStatusListener(updateStatus).then((remove) => {
      if (active) removeConnectionListener = remove;
      else void remove();
    });

    return () => {
      active = false;
      void removeConnectionListener();
    };
  }, []);

  useEffect(() => {
    if (!isAndroid) return undefined;

    let active = true;
    let removeBackListener = () => {};
    const onBack = async ({ canGoBack }) => {
      if (!active || typeof window === 'undefined') return;

      // Pages with an open modal/overlay can call preventDefault() on this
      // event and close themselves before the route or Android app changes.
      const pageEvent = new CustomEvent(NATIVE_BACK_EVENT, {
        cancelable: true,
        detail: { canGoBack: Boolean(canGoBack) },
      });
      window.dispatchEvent(pageEvent);
      if (pageEvent.defaultPrevented) return;

      const currentPath = locationRef.current?.pathname || '/';
      if (canGoBack) {
        navigate(-1);
        return;
      }

      if (currentPath !== '/' && currentPath !== '/login') {
        navigate('/', { replace: true });
        return;
      }

      await minimizeNativeApp();
    };

    void addNativeBackButtonListener(onBack).then((remove) => {
      if (active) removeBackListener = remove;
      else void remove();
    });

    return () => {
      active = false;
      void removeBackListener();
    };
  }, [navigate]);

  useEffect(() => {
    if (!isNative || typeof document === 'undefined') return undefined;

    const onDocumentClick = (event) => {
      if (event.defaultPrevented || (event.button != null && event.button !== 0) || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!shouldOpenLinkExternally(anchor)) return;

      event.preventDefault();
      void openExternal(anchor.href);
    };

    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, []);

  if (!isNative || isConnected) return null;

  return (
    <div style={offlineBannerStyle} role="status" aria-live="polite">
      Sem conexão. A Athena reconecta quando a internet voltar.
    </div>
  );
}

export default NativeAppShell;
